import { FC, ReactNode, useMemo } from 'react'

import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useAtomValue } from 'jotai'
import {
  Text,
  ErrorMessage,
  GreyGrey60,
  Primary13SemiBold,
  Spinner,
  Center,
  Flex
} from '@sequencehq/core-components'
import { useSequenceInfiniteQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useAutomationId } from 'Automations/automationEditor/hooks'
import { editingVersionAtom } from 'Automations/automationEditor/atoms'
import { SidebarTemplate } from '../../components'
import { VersionHistoryItem } from './VersionHistoryItem'

export const PastVersions: FC = () => {
  const { automationId, isNewAutomation } = useAutomationId()
  const editingVersion = useAtomValue(editingVersionAtom)

  const { infiniteQuery } = useSequenceInfiniteQuery(
    dashboardv99990101Client.getAutomationVersions,
    { automationId: automationId ?? '' },
    { enabled: !!automationId && !isNewAutomation }
  )

  const items = useMemo(() => {
    return (
      infiniteQuery.data?.pages
        .flatMap(({ items }) => items)
        .filter(
          ({ id, archivedAt }) => id !== editingVersion?.id && !archivedAt
        )
        // Order purely by creation date (newest first) rather than the
        // endpoint's order, which returns the published version first.
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ) ?? []
    )
  }, [infiniteQuery.data, editingVersion?.id])

  const [infiniteRef] = useInfiniteScroll({
    loading: infiniteQuery.isFetching,
    hasNextPage: infiniteQuery.hasNextPage,
    onLoadMore: infiniteQuery.fetchNextPage,
    rootMargin: `0px 0px 50px 0px`
  })

  // Show the loading state on the first load (isPending) and on any refetch
  // after mount — e.g. after archiving a version while the history is open — so
  // the list doesn't flicker with stale items. Next-page fetches are excluded
  // so infinite scroll keeps the already-loaded items visible.
  const isLoadingList =
    infiniteQuery.isPending ||
    (infiniteQuery.isFetching && !infiniteQuery.isFetchingNextPage)

  if (infiniteQuery.isError) {
    return (
      <Wrapper>
        <SidebarTemplate.Section data-testid="automations.editor.versionHistory.pastVersions.error">
          <ErrorMessage />
        </SidebarTemplate.Section>
      </Wrapper>
    )
  }

  if (isLoadingList) {
    return (
      <Wrapper>
        <Center data-testid="automations.editor.versionHistory.pastVersions.loading">
          <Spinner height="16px" width="16px" />
        </Center>
      </Wrapper>
    )
  }

  if (items.length === 0) return null

  return (
    <Wrapper>
      {items.map(item => (
        <VersionHistoryItem key={item.id} automationVersion={item} />
      ))}

      {infiniteQuery.hasNextPage && (
        <Center
          data-testid="automations.editor.versionHistory.pastVersions.loadMore"
          ref={infiniteRef}
        >
          <Spinner height="16px" width="16px" />
        </Center>
      )}
    </Wrapper>
  )
}

const Wrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Flex
      data-testid="automations.editor.versionHistory.pastVersions"
      flexDir="column"
      gap="6px"
    >
      <SidebarTemplate.Section>
        <Text
          data-testid="automations.editor.versionHistory.pastVersions.label"
          {...Primary13SemiBold}
          color={GreyGrey60}
        >
          Past versions
        </Text>
      </SidebarTemplate.Section>

      <Flex flexDir="column">{children}</Flex>
    </Flex>
  )
}
